import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Keyboard, X, Check } from "lucide-react";

/* Keyboard shortcuts guide (3-Nov-2026). A hands-on cheat sheet: while it is
   open, every shortcut key is caught here instead of acting on the app, so
   people can press keys to see what they do without anything changing. The
   pressed key's row lights up and the keycap clicks down; tried keys get a
   tick. Opened from the Search tip, the user menu or the "?" key.
   Keep the lists in step with the key handler in Layout.js. */

const STAGE_DOT = {
  "PO Received": "#3b82f6", Procurement: "#f97316", Manufacturing: "#a855f7",
  "Quality Control": "#14b8a6", Dispatch: "#f59e0b", Completed: "#94a3b8",
};

export const buildShortcutGroups = (mod) => [
  {
    title: "Search",
    rows: [
      { id: "mod-k", keys: [mod, "K"], label: "Search anything", tip: "Opens search from any page" },
      { id: "/", keys: ["/"], label: "Search (one key)", tip: "Same search, one key" },
      { id: "escape", keys: ["Esc"], label: "Close or cancel", tip: "Closes search, menus and this guide" },
    ],
  },
  {
    title: "Go to",
    rows: [
      { id: "h", keys: ["H"], label: "Dashboard" },
      { id: "r", keys: ["R"], label: "PO Received", stage: "PO Received" },
      { id: "p", keys: ["P"], label: "Procurement", stage: "Procurement" },
      { id: "m", keys: ["M"], label: "Manufacturing", stage: "Manufacturing" },
      { id: "q", keys: ["Q"], label: "QC Check", stage: "Quality Control" },
      { id: "d", keys: ["D"], label: "Dispatch", stage: "Dispatch" },
      { id: "c", keys: ["C"], label: "Completed", stage: "Completed" },
      { id: "l", keys: ["L"], label: "History Log" },
    ],
  },
  {
    title: "Filter",
    rows: [
      { id: "b", keys: ["B"], label: "Blocked only", tip: "Press again to turn it off" },
      { id: "o", keys: ["O"], label: "Overdue only", tip: "Press again to turn it off" },
      { id: "e", keys: ["E"], label: "Expedite only", tip: "Press again to turn it off" },
      { id: "x", keys: ["X"], label: "Clear all filters" },
    ],
  },
  {
    title: "View",
    rows: [
      { id: "n", keys: ["N"], label: "Switch theme", tip: "Light, Auto, Dark" },
      { id: "z", keys: ["Z"], label: "Compact rows", tip: "Fits more rows on screen" },
      { id: "?", keys: ["?"], label: "This guide" },
    ],
  },
];

export default function KeyboardShortcutsGuide({ open, onClose, mod = "⌘", canGo = () => true }) {
  const groups = useMemo(
    () => buildShortcutGroups(mod).map(g => ({ ...g, rows: g.rows.filter(r => !r.stage || canGo(r.stage)) })),
    [mod, canGo]
  );
  const allIds = useMemo(() => groups.flatMap(g => g.rows.map(r => r.id)), [groups]);
  const [lit, setLit] = useState(null);          // { id, seq } of the key just pressed
  const [tried, setTried] = useState(() => new Set());
  const seq = useRef(0);
  const litT = useRef(null);

  useEffect(() => { if (!open) { setLit(null); setTried(new Set()); } }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // Capture phase on window runs before the app's own key handler, so the
    // guide can swallow shortcut keys and keep the page still while practising.
    const onKey = (e) => {
      let id = null;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") id = "mod-k";
      else if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); onClose(); return; }
      else if (e.metaKey || e.ctrlKey || e.altKey) return;
      else if (e.key === "/" || e.key === "?") id = e.key;
      else id = e.key.toLowerCase();
      if (!allIds.includes(id)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      seq.current += 1;
      setLit({ id, seq: seq.current });
      setTried(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
      clearTimeout(litT.current);
      litT.current = setTimeout(() => setLit(null), 1800);
    };
    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); clearTimeout(litT.current); };
  }, [open, allIds, onClose]);

  if (!open) return null;
  const litRow = lit && groups.flatMap(g => g.rows).find(r => r.id === lit.id);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[130] rl-palette-scrim" onClick={onClose} data-testid="shortcuts-guide-backdrop" />
      <div
        className="rl-kbd-guide fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[140] w-[min(720px,94vw)] max-h-[88vh] overflow-auto rounded-[20px] border border-[hsl(var(--border))] bg-[hsl(var(--card))]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rl-kbd-guide-title"
        data-testid="shortcuts-guide"
      >
        <div className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-[hsl(var(--border))]">
          <span className="rl-kbd-guide-icon w-9 h-9 rounded-xl flex items-center justify-center shrink-0" aria-hidden="true">
            <Keyboard className="w-[18px] h-[18px]" strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 id="rl-kbd-guide-title" className="text-[15px] font-semibold text-[hsl(var(--foreground))]">Get around RockLink with your keyboard</h2>
            <p className="text-[12.5px] text-[hsl(var(--muted-foreground))] mt-0.5">
              Try it now: press any key below. Nothing on the page changes while this is open.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))] shrink-0"
            aria-label="Close keyboard shortcuts"
            data-testid="shortcuts-guide-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Live practice line: what the key you just pressed would do. */}
        <div className="px-6 pt-4" aria-live="polite">
          <div className={`rl-kbd-practice${litRow ? " on" : ""} flex items-center gap-2.5 h-10 px-3 rounded-xl text-[12.5px]`} data-testid="shortcuts-guide-practice">
            {litRow ? (
              <>
                <span className="flex items-center gap-1">
                  {litRow.keys.map(k => <kbd key={k} className="rl-kbd-cap sm">{k}</kbd>)}
                </span>
                <span className="text-[hsl(var(--foreground))] font-medium">
                  {litRow.id === "?" ? "Opens this guide" : litRow.id === "/" || litRow.id === "mod-k" ? "Opens search" : litRow.stage || ["h", "l"].includes(litRow.id) ? `Jumps to ${litRow.label}` : litRow.label}
                </span>
                {litRow.tip && <span className="text-[hsl(var(--muted-foreground))]">· {litRow.tip}</span>}
              </>
            ) : (
              <span className="text-[hsl(var(--muted-foreground))]">
                {tried.size === 0 ? "Waiting for a key…" : "Press another key"}
              </span>
            )}
            <span className="ml-auto text-[11px] font-medium text-[hsl(var(--muted-foreground))] tabular-nums">
              {tried.size} of {allIds.length} tried
            </span>
          </div>
        </div>

        {/* Two columns: Search, Filter and View on the left, Go to on the right. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 px-6 pt-4 pb-5">
          {[groups.filter(g => g.title !== "Go to"), groups.filter(g => g.title === "Go to")].map((col, ci) => (
          <div key={ci} className="flex flex-col gap-4">
          {col.map(g => (
            <section key={g.title}>
              <h3 className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))] mb-1.5">{g.title}</h3>
              <ul>
                {g.rows.map(r => {
                  const isLit = lit && lit.id === r.id;
                  return (
                    <li
                      key={r.id}
                      className={`rl-kbd-row${isLit ? " lit" : ""} flex items-center gap-3 h-8 px-2 -mx-2 rounded-lg`}
                      data-testid={`shortcut-row-${r.id}`}
                    >
                      <span className="flex items-center gap-1 w-[64px] shrink-0">
                        {r.keys.map(k => (
                          <kbd key={isLit ? `${k}-${lit.seq}` : k} className={`rl-kbd-cap${isLit ? " pressed" : ""}`}>{k}</kbd>
                        ))}
                      </span>
                      {r.stage && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STAGE_DOT[r.stage] }} aria-hidden="true" />}
                      <span className="flex-1 min-w-0 truncate text-[13px] text-[hsl(var(--foreground))]">{r.label}</span>
                      {tried.has(r.id) && <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" strokeWidth={2.6} aria-label="Tried" />}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          </div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-6 py-3.5 border-t border-[hsl(var(--border))]">
          <span className="text-[12px] text-[hsl(var(--muted-foreground))]">
            Letter keys work anywhere except while you're typing in a box. Open this guide any time with <kbd className="rl-kbd-cap sm">?</kbd>
          </span>
          <button
            onClick={onClose}
            className="ml-auto h-8 px-4 rounded-full text-[12.5px] font-semibold text-white bg-[#f97316] hover:bg-[#ea580c] transition-colors shrink-0"
            data-testid="shortcuts-guide-done"
          >
            Done
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
